import { useEffect, useState } from 'react';

// Real, read-only facts about the visitor's browser — nothing here is faked.
// Anything a browser doesn't expose comes back as null and is simply hidden.

function browserName() {
    const ua = navigator.userAgent;
    const brands = navigator.userAgentData?.brands?.map((b) => b.brand) || [];
    const brand = brands.find((b) => !/not.?a.?brand|chromium/i.test(b));
    if (brand) {
        const v = navigator.userAgentData.brands.find((b) => b.brand === brand)?.version;
        return `${brand} ${v}`;
    }
    const m = ua.match(/(Edg|OPR|Firefox|CriOS|FxiOS|Chrome)\/(\d+)/);
    if (m) {
        const names = { Edg: 'Edge', OPR: 'Opera', CriOS: 'Chrome', FxiOS: 'Firefox' };
        return `${names[m[1]] || m[1]} ${m[2]}`;
    }
    const safari = ua.match(/Version\/(\d+).*Safari/);
    return safari ? `Safari ${safari[1]}` : 'unknown';
}

function osName() {
    const p = navigator.userAgentData?.platform || navigator.platform || '';
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/i.test(p)) return 'macOS';
    if (/Win/i.test(p)) return 'Windows';
    if (/Linux/i.test(p)) return 'Linux';
    return p || 'unknown';
}

export function systemInfo() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return {};
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    const conn = navigator.connection;
    return {
        browser: browserName(),
        os: osName(),
        cores: navigator.hardwareConcurrency || null,
        memory: navigator.deviceMemory || null,
        screen: `${window.screen.width}×${window.screen.height}`,
        viewport: `${window.innerWidth}×${window.innerHeight}`,
        dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lang: navigator.language,
        network: conn?.effectiveType ? `${conn.effectiveType}${conn.downlink ? ` · ${conn.downlink} Mb/s` : ''}` : null,
        loadMs: nav?.domContentLoadedEventEnd ? Math.round(nav.domContentLoadedEventEnd) : null,
    };
}

export function heapMB() {
    const m = typeof performance !== 'undefined' && performance.memory;
    return m ? Math.round(m.usedJSHeapSize / 1048576) : null;
}

export function formatUptime(ms) {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : m ? `${m}m ${s % 60}s` : `${s}s`;
}

// Seconds since the page started loading, ticking once a second.
export function useUptime() {
    const [ms, setMs] = useState(() => (typeof performance !== 'undefined' ? performance.now() : 0));
    useEffect(() => {
        const id = setInterval(() => setMs(performance.now()), 1000);
        return () => clearInterval(id);
    }, []);
    return ms;
}

// Measured frames per second, sampled every 500ms, with a short history for a sparkline.
export function useFps(samples = 36) {
    const [state, setState] = useState({ fps: null, history: [] });
    useEffect(() => {
        if (typeof requestAnimationFrame === 'undefined') return undefined;
        let frames = 0;
        let last = performance.now();
        let raf = 0;
        const loop = (now) => {
            frames += 1;
            if (now - last >= 500) {
                const fps = Math.round((frames * 1000) / (now - last));
                frames = 0;
                last = now;
                setState((s) => ({ fps, history: [...s.history, fps].slice(-samples) }));
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [samples]);
    return state;
}
